import { FC, useEffect, useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useRouter } from 'next/router';
import { Button, LinkButton, SvgHelperWrapper } from 'src/components';
import { Buddy } from 'src/api/buddy/types';
import { useUserContext } from 'src/context/user';
import { resolveHref } from 'next/dist/client/resolve-href';
import { ROUTES } from 'src/constants/routes.const';
import { isProduction } from 'src/utils/env/isProduction';
import { randomInteger } from 'src/utils/numbers';
import Stars from './stars.svg';
import * as S from './styles';

interface Props {
  isOpen: boolean;
  isLoading: boolean;
  handleClose: () => void;
  buddies: Buddy[];
}

const msgs = defineMessages({
  maybeLaterCta: { id: 'plans_page_interstitial_maybe_later' },
  startCta: { id: 'homeScreen_miniPlayer_start_CTA' },
  upgradeCta: { id: 'ai_buddies_locked_title' },
});

export function getWeightedRandomBuddy(buddies: Buddy[]): Buddy | null {
  const availableBuddies = buddies.filter(
    buddy => (!isProduction() || buddy.useInProd) && buddy.recommendationWeight,
  );
  const totalWeight = availableBuddies.reduce((sum, buddy) => sum + buddy.recommendationWeight, 0);
  const rand = randomInteger({ min: 0, max: totalWeight });

  let selectedBuddy: Buddy | null = null;
  availableBuddies.reduce((cumulativeWeight, buddy) => {
    if (!selectedBuddy && rand < cumulativeWeight + buddy.recommendationWeight) {
      selectedBuddy = buddy;
    }
    return cumulativeWeight + buddy.recommendationWeight;
  }, 0);

  return selectedBuddy;
}

export const BuddyRecommendationModal: FC<Props> = ({
  isOpen,
  isLoading,
  handleClose,
  buddies,
}) => {
  const intl = useIntl();
  const router = useRouter();
  const { user } = useUserContext();
  const recommendedBuddy = useMemo(() => getWeightedRandomBuddy(buddies), [buddies]);

  useEffect(() => {
    if (isOpen && !isLoading && !recommendedBuddy) {
      handleClose();
    }
  }, [isOpen, isLoading, recommendedBuddy, handleClose]);

  if (!recommendedBuddy) {
    return null;
  }

  return (
    <S.ModalWrapper isOpen={isOpen} handleClose={handleClose} hideCloseButton>
      <S.Content $spacing="s6" $align="center">
        <S.ImageWrapper>
          <S.StarsContainer>
            <SvgHelperWrapper Svg={Stars} data-testid="pro-card-stars" />
          </S.StarsContainer>
          <S.ImageCircle $color={recommendedBuddy.baseColor} />
          <S.Image src={recommendedBuddy.imageUrl} />
        </S.ImageWrapper>
        <S.Title>{recommendedBuddy.name}</S.Title>
        <S.Description>{recommendedBuddy.upgradeMessage}</S.Description>
        <S.Actions>
          <Button buttonType="secondary-dark" buttonSize="medium" wide onClick={handleClose}>
            {intl.formatMessage(msgs.maybeLaterCta)}
          </Button>
          {recommendedBuddy.isPremium && !user?.isPro ? (
            <LinkButton buttonType="primary" buttonSize="medium" wide href={ROUTES.payment.plans}>
              {recommendedBuddy.upgradeCta}
            </LinkButton>
          ) : (
            <LinkButton
              buttonType="primary"
              buttonSize="medium"
              wide
              href={resolveHref(router, {
                pathname: ROUTES.ai.dialog,
                query: {
                  type: recommendedBuddy.id,
                },
              })}
            >
              {intl.formatMessage(msgs.startCta)}
            </LinkButton>
          )}
        </S.Actions>
      </S.Content>
    </S.ModalWrapper>
  );
};
